// Curated static-data layer over generated.ts: types, lookups, exclusivity
// rules, stock-race presets, and the DATA_VERSION fingerprint.
//
// Identity rules (see README.md): string ids are canonical; APPLICATION_ROWS is
// authoritative for tech-tree structure; numeric techIds are reference only.

import { hashCanonical } from '../canonical';
import {
  APPLICATION_ROWS,
  BUILDABLE_ROWS,
  CP_SOURCES,
  CP_USAGE,
  FIELD_ROWS,
  FIELD_SUBJECTS,
  HULL_ROWS,
  LANDER_COSTS,
  MISC_COSTS,
  MOD_FLAG_KEYS,
  PICK_BUDGET,
  PICK_ROWS,
  SCAN_BONUSES,
  STARTING_FIELD_NUMS,
  STEALTH_BONUSES,
  TECH_ROWS,
  WEAPON_MOD_ROWS,
  WEAPON_ROWS,
  type ApplicationRow,
  type BuildableRow,
  type FieldRow,
  type HullRow,
  type PickRow,
  type TechRow,
  type WeaponModRow,
  type WeaponRow,
} from './generated';

export * from './generated';

// ---------- subjects ----------

export const SUBJECTS = [
  'construction',
  'power',
  'chemistry',
  'sociology',
  'computers',
  'ecology',
  'physics',
  'force_fields',
] as const;
export type Subject = (typeof SUBJECTS)[number];

// ---------- lookups ----------

function byId<T extends { id: string }>(rows: readonly T[], what: string): ReadonlyMap<string, T> {
  const m = new Map<string, T>();
  for (const r of rows) {
    if (m.has(r.id)) throw new Error(`duplicate ${what} id: ${r.id}`);
    m.set(r.id, r);
  }
  return m;
}

export const pickById: ReadonlyMap<string, PickRow> = byId(PICK_ROWS, 'pick');
export const fieldById: ReadonlyMap<string, FieldRow> = byId(FIELD_ROWS, 'field');
export const techById: ReadonlyMap<string, TechRow> = byId(TECH_ROWS, 'tech');

/** Curated buildable projects the source tables list only as applications.
 * Terraform costs are OUR tunables (T1, data/README.md); the terraforming
 * project's cost is dynamic (rises per completed step — items.ts). */
export const CURATED_BUILDABLES: BuildableRow[] = [
  { id: 'terraforming', techId: 184, cost: 250, maintenance: 0, group: 'special' },
  { id: 'gaia_transformation', techId: 211, cost: 500, maintenance: 0, group: 'special' },
  // researchable applications with live effect handlers that the source tables
  // never listed as buildable (costs from the classic tables)
  { id: 'space_port', techId: 163, cost: 80, maintenance: 1, group: 'economy' },
  { id: 'pollution_processor', techId: 141, cost: 80, maintenance: 1, group: 'production' },
  { id: 'armor_barracks', techId: 0, cost: 75, maintenance: 2, group: 'government' },
  { id: 'recyclotron', techId: 0, cost: 200, maintenance: 3, group: 'production' },
  { id: 'robotic_factory', techId: 0, cost: 200, maintenance: 3, group: 'production' },
];

export const buildableById: ReadonlyMap<string, BuildableRow> = byId(
  [...BUILDABLE_ROWS, ...CURATED_BUILDABLES],
  'buildable',
);
export const hullById: ReadonlyMap<string, HullRow> = byId(HULL_ROWS, 'hull');

/** The Guardian's prize: the source weapon table has no death_ray row (the
 * monster builds its own synthetic spec), but the granted application must
 * map to a mountable weapon or the game's biggest PvE reward is inert. */
export const CURATED_WEAPONS: WeaponRow[] = [
  {
    id: 'death_ray',
    techId: 999, // no research path: granted only by defeating the Guardian
    classId: 0,
    ammo: -1,
    space: 250,
    cost: 300,
    naturalMods: [],
    tacticalDamage: { min: 50, max: 100 },
    strategicDamage: { min: 50, max: 100 },
    availableMods: ['hv'],
  },
];

export const weaponById: ReadonlyMap<string, WeaponRow> = byId([...WEAPON_ROWS, ...CURATED_WEAPONS], 'weapon');
export const weaponModById: ReadonlyMap<string, WeaponModRow> = byId(WEAPON_MOD_ROWS, 'weaponMod');

export const fieldByNum: ReadonlyMap<number, FieldRow> = new Map(
  FIELD_ROWS.map((f) => [f.num, f]),
);

/** Applications may repeat per field only via hyper-advanced synthetics; base rows are unique. */
export const applicationById: ReadonlyMap<string, ApplicationRow> = byId(
  APPLICATION_ROWS,
  'application',
);

/** Curated descriptions for items with NO application row (always-known
 * basics etc.) — display-only, so no DATA_VERSION impact. Text mirrors the
 * actual engine mechanics; keep in sync with economy.ts/items.ts. */
export const CURATED_ITEM_DESCRIPTIONS: Readonly<Record<string, string>> = {
  capitol: 'Seat of government: +10 morale at this colony and the hub conquered races assimilate through.',
  marine_barracks:
    'Trains one marine per 5 turns (garrison cap +4) and lifts the no-barracks morale penalty. Marines defend against invasion and board transports (4 per squad).',
  spy: 'Trains one agent for espionage or counter-intelligence (roster cap 10).',
  housing: 'The colony downs tools and builds homes: all production accelerates population growth while this stays at the head of the queue.',
  trade_goods: 'Converts all production to income at 2 prod → 1 BC (fantastic traders: 1:1) while this stays at the head of the queue.',
};

/** UI description of a buildable/researchable item: the application row's
 * effect summary when one exists, curated text otherwise. */
export function itemDescription(itemId: string): string | null {
  return applicationById.get(itemId)?.effectSummary || CURATED_ITEM_DESCRIPTIONS[itemId] || null;
}

/** Weapon id -> granting application id where naive pluralization fails
 * (disrupter/disruptor spelling drift; torpedo + 's' != torpedoes). */
export const WEAPON_APP_ALIAS: Readonly<Record<string, string>> = {
  disrupter: 'disruptor_cannon',
  proton_torpedo: 'proton_torpedoes',
  plasma_torpedo: 'plasma_torpedoes',
};

/** The application that grants a weapon (id join with alias + plural forms). */
export function appForWeapon(weaponId: string): ApplicationRow | undefined {
  const alias = WEAPON_APP_ALIAS[weaponId];
  return (
    (alias ? applicationById.get(alias) : undefined) ??
    applicationById.get(weaponId) ??
    applicationById.get(weaponId + 's')
  );
}

export function applicationsOfField(fieldId: string): ApplicationRow[] {
  return APPLICATION_ROWS.filter((a) => a.fieldId === fieldId);
}

export function subjectOfField(fieldId: string): string {
  const s = FIELD_SUBJECTS[fieldId];
  if (!s) throw new Error(`no subject for field ${fieldId}`);
  return s;
}

/** Ordered researchable fields of a subject (by walking next-chain from root). */
export function fieldsOfSubject(subject: Subject): FieldRow[] {
  const out: FieldRow[] = [];
  for (const f of FIELD_ROWS) if (FIELD_SUBJECTS[f.id] === subject) out.push(f);
  out.sort((a, b) => (a.tier - b.tier) || (a.cost - b.cost) || (a.num - b.num));
  return out;
}

// ---------- picks: groups, exclusivity, budget ----------

export const GOVERNMENTS = ['feudal', 'dictatorship', 'democracy', 'unification'] as const;
export type Government = (typeof GOVERNMENTS)[number];

/** Mutually exclusive pick groups (at most one per group; government exactly one). */
export const PICK_EXCLUSIVE_GROUPS: Readonly<Record<string, readonly string[]>> = {
  government: GOVERNMENTS,
  growth: ['growth1', 'growth2', 'growth3'],
  farming: ['farming1', 'farming2', 'farming3'],
  industry: ['industry1', 'industry2', 'industry3'],
  science: ['science1', 'science2', 'science3'],
  money: ['money1', 'money2', 'money3'],
  defense: ['defense1', 'defense2', 'defense3'],
  attack: ['attack1', 'attack2', 'attack3'],
  ground: ['ground1', 'ground2', 'ground3'],
  spying: ['spying1', 'spying2', 'spying3'],
  gravity: ['lowg_world', 'highg_world'],
  hw_minerals: ['rich_hw', 'poor_hw'],
  creativity: ['creative', 'uncreative'],
  charisma: ['charismatic', 'repulsive'],
};

export const MAX_POSITIVE_PICKS = PICK_BUDGET['maximum_positive_custom_race_picks'] ?? 10;
export const MAX_NEGATIVE_PICKS = PICK_BUDGET['maximum_negative_picks'] ?? -10;
export const ADAPTIVE_UPGRADE_PICKS = PICK_BUDGET['adaptive_upgrade_added_picks'] ?? 4;

export interface PickValidationResult {
  ok: boolean;
  errors: string[];
  cost: number;
}

/** Validate a custom race pick set per the §02 rules. */
export function validatePicks(pickIds: readonly string[], budget: number = MAX_POSITIVE_PICKS): PickValidationResult {
  const errors: string[] = [];
  const seen = new Set<string>();
  let cost = 0;
  for (const id of pickIds) {
    const row = pickById.get(id);
    if (!row) {
      errors.push(`unknown pick: ${id}`);
      continue;
    }
    if (seen.has(id)) errors.push(`duplicate pick: ${id}`);
    seen.add(id);
    cost += row.cost;
  }
  for (const [group, members] of Object.entries(PICK_EXCLUSIVE_GROUPS)) {
    const chosen = members.filter((m) => seen.has(m));
    if (group === 'government') {
      if (chosen.length !== 1) errors.push(`exactly one government required (got ${chosen.length})`);
    } else if (chosen.length > 1) {
      errors.push(`picks are mutually exclusive: ${chosen.join(', ')}`);
    }
  }
  // Negative picks ADD budget: net cost must stay within [-10, +10] and at most
  // 10 points of disadvantages may be taken (stock presets like the telepathic
  // feudal race spend 14 gross positives against 4 refunded points, legally).
  const negatives = pickIds.reduce((s, id) => s + Math.min(0, pickById.get(id)?.cost ?? 0), 0);
  if (negatives < MAX_NEGATIVE_PICKS) {
    errors.push(`negative picks ${negatives} exceed allowance ${MAX_NEGATIVE_PICKS}`);
  }
  if (cost > budget) errors.push(`net cost ${cost} exceeds ${budget}`);
  return { ok: errors.length === 0, cost, errors };
}

// ---------- stock race presets ----------
//
// Presets correspond row-by-row to mechanics/races.md (same pick sets); display
// names here are original to this project, per the docs' renaming guidance.

export interface RacePreset {
  id: string;
  name: string;
  picks: readonly string[];
}

export const RACE_PRESETS: readonly RacePreset[] = [
  { id: 'skyshear', name: 'Skyshear Flock', picks: ['defense3', 'arti_world', 'dictatorship'] },
  { id: 'urgok', name: 'Urgok Clans', picks: ['attack2', 'ground2', 'highg_world', 'dictatorship'] },
  { id: 'veilkin', name: 'Veilkin Syndics', picks: ['spying3', 'stealthy_ships', 'dictatorship'] },
  { id: 'mindral', name: 'Mindral Court', picks: ['defense2', 'attack2', 'telepathic', 'omniscient', 'feudal'] },
  { id: 'lumini', name: 'Lumini Compact', picks: ['money3', 'lowg_world', 'fantastic_traders', 'lucky', 'dictatorship'] },
  { id: 'solari', name: 'Solari Republic', picks: ['charismatic', 'democracy'] },
  { id: 'hivex', name: 'Hivex Commune', picks: ['farming2', 'industry2', 'large_hw', 'uncreative', 'unification'] },
  { id: 'ferron', name: 'Ferron Assembly', picks: ['industry3', 'cybernetic', 'dictatorship'] },
  { id: 'korrath', name: 'Korrath Prides', picks: ['attack3', 'rich_hw', 'warlord', 'dictatorship'] },
  { id: 'cerebri', name: 'Cerebri Conclave', picks: ['science3', 'lowg_world', 'large_hw', 'creative', 'dictatorship'] },
  { id: 'sauren', name: 'Sauren Broods', picks: ['growth3', 'farming2', 'spying1', 'subterranean', 'large_hw', 'feudal'] },
  { id: 'lithor', name: 'Lithor Shards', picks: ['growth1', 'lithovore', 'repulsive', 'tolerant', 'dictatorship'] },
  { id: 'tidari', name: 'Tidari Currents', picks: ['aquatic', 'trans_dimensional', 'dictatorship'] },
];

export const racePresetById: ReadonlyMap<string, RacePreset> = byId(RACE_PRESETS, 'racePreset');

// ---------- starting knowledge ----------

/** Start-mode field tables from the source data. NOTE: since the pre-warp
 * rework these are a data LOOKUP, not the literal grant — the pre_warp mode
 * itself starts with ONLY Engineering completed (construction basics);
 * 'average'/'advanced' union these tables into their head-start supersets
 * (adapter.ts initGame). */
export function startingFieldNums(mode: 'pre_warp' | 'average'): readonly number[] {
  const nums = STARTING_FIELD_NUMS[mode];
  if (!nums) throw new Error(`unknown start mode ${mode}`);
  return nums;
}

/** Items every empire knows regardless of research (see mechanics/unresearchable.md). */
/** Items every empire can build from turn 1 (no unlocking application exists
 * for these: the classic always-available projects + starting structures). */
export const ALWAYS_KNOWN_ITEMS = ['capitol', 'marine_barracks', 'spy', 'housing', 'trade_goods'] as const;

// ---------- version fingerprint ----------

export const DATA_VERSION: string = hashCanonical({
  picks: PICK_ROWS,
  buildables: BUILDABLE_ROWS,
  fields: FIELD_ROWS,
  subjects: FIELD_SUBJECTS,
  techs: TECH_ROWS,
  hulls: HULL_ROWS,
  weapons: WEAPON_ROWS,
  weaponMods: WEAPON_MOD_ROWS,
  modFlags: MOD_FLAG_KEYS,
  applications: APPLICATION_ROWS,
  starting: STARTING_FIELD_NUMS,
  budget: PICK_BUDGET,
  cpSources: CP_SOURCES,
  cpUsage: CP_USAGE,
  scan: SCAN_BONUSES,
  stealth: STEALTH_BONUSES,
  landers: LANDER_COSTS,
  misc: MISC_COSTS,
  presets: RACE_PRESETS,
});
